import type { QueryRowType } from "@rocicorp/zero";
import { useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { config } from "../../environment";
import { useCreateSkill } from "../../hooks/useCreateSkill";
import { useQuery } from "../../hooks/useQuery";
import { useUrlSort } from "../../hooks/useUrlSort";
import { getWritableScopeIds } from "../../lib/getWritableScopeIds";
import { makeUrl, navigate, routes, useCurrentUrl } from "../../lib/urls";
import { userDisplayName } from "../../lib/userDisplayName";
import { Avatar } from "../Avatar";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { ConnectorLogoContainer } from "../ConnectorLogoContainer";
import { Dropdown } from "../Dropdown";
import { EmptyState } from "../EmptyState";
import { Icon } from "../Icon";
import {
  type FilterDef,
  hasActiveFilters,
  ListFilter,
  ListFilterPills,
  parseMultiFilter,
  toggleMultiFilter,
} from "../ListFilter";
import { ListTable } from "../ListTable";
import { SkillStatusPill } from "../SkillStatusPill";
import { Text } from "../Text";
import { Toggle } from "../Toggle";
import { SkillsLayout } from "./SkillsLayout";
import { InstallGitSkillsModal } from "./InstallGitSkillsModal";

type SkillRow = QueryRowType<ReturnType<typeof queries.skillsManagementIndex>>;
type SkillCreator = NonNullable<SkillRow["createdBy"]>;

export function Custom() {
  const z = useZero();
  const { canCreate, createSkill } = useCreateSkill();
  const openNewSkill = () => {
    const id = createSkill();
    if (id) navigate(routes.modal.skill, { id, tab: "files" });
  };
  const [skillsResult, skillsDetails] = useQuery(
    queries.skillsManagementIndex()
  );
  const managedSkills = useMemo(
    () =>
      skillsResult.filter(
        (skill) => !skill.fresh || skill.gitInstallation !== undefined
      ),
    [skillsResult]
  );
  const { q: query, creator: creatorParam } = useCurrentUrl(routes.skills.list);
  const creatorFilters = parseMultiFilter(creatorParam);
  const { sortColumn, sortDirection, handleSort, sortableColumns } = useUrlSort(
    routes.skills.list,
    {
      name: "asc",
      addedBy: "asc",
    }
  );
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [installOpen, setInstallOpen] = useState(false);

  const [myGrants] = useQuery(queries.currentUserGrants());
  const writableIds = useMemo(
    () =>
      getWritableScopeIds(
        myGrants ?? [],
        managedSkills.map((skill) => skill.id),
        "skill:write"
      ),
    [managedSkills, myGrants]
  );

  const queryFiltered = useMemo(() => {
    if (!query) return managedSkills;
    const lower = query.toLowerCase();
    return managedSkills.filter(
      (skill) =>
        skill.name.toLowerCase().includes(lower) ||
        skill.description.toLowerCase().includes(lower)
    );
  }, [managedSkills, query]);

  const availableCreators = new Map<string, SkillCreator>();
  for (const skill of queryFiltered) {
    if (skill.createdByUserId && skill.createdBy) {
      availableCreators.set(skill.createdByUserId, skill.createdBy);
    }
  }
  const availableCreatorOptions = [...availableCreators.values()].sort((a, b) =>
    userDisplayName(a).localeCompare(userDisplayName(b))
  );

  const skills = queryFiltered.filter((skill) => {
    if (creatorFilters.length > 0) {
      if (
        !skill.createdByUserId ||
        !creatorFilters.includes(skill.createdByUserId)
      ) {
        return false;
      }
    }
    return true;
  });

  const filters: FilterDef[] = [
    {
      kind: "multi",
      key: "creator",
      label: "Added by",
      icon: <Icon name="circle-user-round" size={16} />,
      options: availableCreatorOptions.map((creator) => ({
        id: creator.id,
        label: userDisplayName(creator),
        menuLeft: <Avatar user={creator} size={16} />,
        pillLeft: <Avatar user={creator} size={12} />,
      })),
      values: creatorFilters,
      onToggle: (id) =>
        navigate(
          null,
          { creator: toggleMultiFilter(creatorFilters, id) },
          { replace: true }
        ),
    },
  ];

  const sortedSkills = [...skills].sort((a, b) => {
    if (!sortColumn) return b.createdAt - a.createdAt;
    let aVal: string | number;
    let bVal: string | number;
    switch (sortColumn) {
      case "name":
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case "addedBy":
        aVal = a.createdBy ? userDisplayName(a.createdBy).toLowerCase() : "";
        bVal = b.createdBy ? userDisplayName(b.createdBy).toLowerCase() : "";
        break;
    }
    if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const toggleEnabled = (skill: SkillRow) => {
    z.mutate(
      sharedMutators.updateSkill({
        skillId: skill.id,
        name: skill.name,
        description: skill.description,
        instructions: skill.instructions,
        enabled: !(skill.enabled ?? true),
      })
    );
  };

  return (
    <>
      <title>{`Skills • ${config.title}`}</title>
      <SkillsLayout
        searchAction={
          canCreate ? (
            <Dropdown
              opener={<Button size="l" icon="plus" />}
              minWidth={200}
              sections={[
                {
                  items: [
                    {
                      type: "button",
                      left: <Icon name="plus" size={16} />,
                      label: "Create from scratch",
                      onClick: openNewSkill,
                    },
                    {
                      type: "button",
                      left: <Icon name="git-branch" size={16} />,
                      label: "Install from Git",
                      onClick: () => setInstallOpen(true),
                    },
                  ],
                },
              ]}
            />
          ) : undefined
        }
      >
        <ListTable
          style={{ borderRadius: 0, borderLeft: 0, borderRight: 0 }}
          columns={{ description: "flex" }}
          footer={
            hasActiveFilters(filters) ? (
              <ListFilterPills filters={filters} />
            ) : null
          }
          headerActions={<ListFilter filters={filters} />}
          emptyState={
            skillsDetails.type === "complete" ? (
              managedSkills.length > 0 ? (
                <EmptyState
                  iconName="graduation-cap"
                  title="No matches"
                  description="Try a different filter."
                />
              ) : !query ? (
                <EmptyState
                  iconName="graduation-cap"
                  title="No skills yet"
                  description="Create a skill, or install one from a Git repository."
                  action={
                    canCreate ? (
                      <Button variant="primary" onClick={openNewSkill}>
                        New skill
                      </Button>
                    ) : undefined
                  }
                />
              ) : undefined
            ) : undefined
          }
          headers={{
            name: "Skill",
            description: "Description",
            addedBy: "Added by",
            status: "Status",
            actions: "",
          }}
          sortableColumns={sortableColumns}
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onSort={handleSort}
          items={sortedSkills.map((skill) => ({
            key: skill.id,
            link: makeUrl(routes.modal.skill, { id: skill.id }),
            name: (hover) => (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  minWidth: 0,
                }}
              >
                <ConnectorLogoContainer
                  size={32}
                  hover={hover}
                  borderRadius={8}
                  outline={false}
                  hoverBackground={false}
                >
                  <Icon name="graduation-cap" size={16} />
                </ConnectorLogoContainer>
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    minWidth: 0,
                  }}
                >
                  <Text
                    ellipsis
                    variant="text-regular-md"
                    color="var(--text-primary)"
                  >
                    {skill.name || "Untitled skill"}
                  </Text>
                  <Text variant="text-small" color="var(--text-secondary)">
                    {skill.gitInstallation
                      ? skill.gitInstallation.syncStartedAt !== null
                        ? "Syncing from Git…"
                        : skill.gitInstallation.lastError
                          ? "Git sync failed"
                          : "Installed from Git"
                      : `Created ${new Date(skill.createdAt).toLocaleDateString()}`}
                  </Text>
                </div>
              </div>
            ),
            description: (
              <Text
                variant="text-small"
                color="var(--text-secondary)"
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {skill.description}
              </Text>
            ),
            status: <SkillStatusPill enabled={skill.enabled ?? true} />,
            addedBy: (
              <Text variant="text-small" color="var(--text-primary)" ellipsis>
                {skill.createdBy ? userDisplayName(skill.createdBy) : undefined}
              </Text>
            ),
            actions: (hasMouse) => {
              const canWrite = writableIds?.has(skill.id) ?? false;
              return (
                <div style={{ opacity: hasMouse ? 1 : 0 }}>
                  <Dropdown
                    opener={
                      <Button
                        variant="secondary"
                        icon="more"
                        disabled={!canWrite}
                      />
                    }
                    minWidth={160}
                    sections={[
                      {
                        items: [
                          {
                            type: "button",
                            keepOpen: true,
                            left: <Icon name="graduation-cap" size={16} />,
                            label: "Available to AI",
                            right: (
                              <Toggle
                                isOn={skill.enabled ?? true}
                                onToggle={() => toggleEnabled(skill)}
                              />
                            ),
                            onClick: () => toggleEnabled(skill),
                          },
                          {
                            type: "button",
                            left: <Icon name="pencil" size={16} />,
                            label: "Edit",
                            onClick: () =>
                              navigate(routes.modal.skill, { id: skill.id }),
                          },
                          {
                            type: "button",
                            left: <Icon name="trash" size={16} />,
                            label: "Delete",
                            onClick: () => setConfirmDeleteId(skill.id),
                          },
                        ],
                      },
                    ]}
                  />
                </div>
              );
            },
          }))}
        />
      </SkillsLayout>
      <ConfirmModal
        open={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          if (confirmDeleteId) {
            z.mutate(sharedMutators.deleteSkill({ skillId: confirmDeleteId }));
          }
          setConfirmDeleteId(null);
        }}
        title="Delete skill?"
        description="Permanently deletes this skill and its access settings."
        confirmLabel="Delete"
        destructive
      />
      {installOpen && (
        <InstallGitSkillsModal
          onClose={() => setInstallOpen(false)}
          onInstalled={() => setInstallOpen(false)}
        />
      )}
    </>
  );
}
